/**
 * Generating the sound library onto the device.
 *
 * The library is three hundred sounds and about eighty megabytes of audio. Rendering it is
 * not expensive — the whole catalogue takes a few seconds on a desktop and well under a
 * minute on a phone — but it is far too much to do between a tap and the first frame, and a
 * user who taps a pad while it happens must still hear a pad.
 *
 * So generation is three things at once:
 *
 * 1. **Staged.** Sixteen drums render first — the cheapest sounds in the library, and the
 *    ones a board is most likely to want — and everything else fills in behind them in pack
 *    order, drums before instruments, so an interrupted first run leaves a complete kit in
 *    the library rather than half an octave of bells. The board itself is never touched;
 *    what makes the app playable on first launch is the template's own starter kit.
 * 2. **Incremental.** Each sound is written to the blob store and added to the project as it
 *    finishes, in small batches. Nothing is held in memory waiting for the batch to complete,
 *    and closing the tab halfway costs only the sound that was rendering.
 * 3. **Resumable.** What is already installed is read back off the project rather than from a
 *    counter, keyed by the library's own file names, so a reload continues where it stopped
 *    and installing a pack that shares sounds with another pack renders nothing twice.
 *
 * Nothing here is on the audio path. Every render goes through a `RenderBudget`, which hands
 * the main thread back between blocks, and the whole run can be stopped from the UI.
 */

import { signal, type ReadonlySignal } from '../core/signal';
import { createLogger, tag } from '../core/log';
import {
  makeSampleAsset,
  sampleEditIdentity,
  type SampleAsset,
  type SampleEdit,
  type UUID,
} from '../model/types';
import { RenderBudget } from '../audio/offline-render';
import { libraryContentLicence, type LibraryEntry } from '../audio/library/entry';
import { libraryInstallOrder, libraryPack, LIBRARY_PACKS } from '../audio/library/packs';
import { libraryAssetKey } from '../storage/db';
import { libraryUUID } from '../audio/library/identity';
import type { RenderedAudio } from '../audio/offline-render';
import {
  analysis,
  blobStore,
  encodeWav,
  engine,
  mutate,
  project,
  pushBanner,
  settings,
  updateSettings,
} from './state';

const log = createLogger('library');

/** Namespace byte for library asset ids, so they cannot collide with any other derived id. */
const LIBRARY_ID_SCOPE = 9;

/** Tag every generated sound carries, so the library is one collection rather than a guess. */
export const LIBRARY_TAG = 'padlab library';

/**
 * Bumped when the catalogue changes in a way that should make an existing install fetch the
 * new sounds. Existing sounds are never replaced — a user may have edited or renamed one —
 * so this only decides whether the background run starts again.
 */
export const LIBRARY_CONTENT_VERSION = 1;

/**
 * How many sounds are written into the project per `mutate`.
 *
 * Every mutation snapshots the project for undo, so one per sound would clone a growing
 * document three hundred times. Twelve keeps the clone count in the twenties while still
 * meaning that a tab closed mid-run loses at most twelve rendered sounds.
 */
const BATCH_SIZE = 12;

/**
 * Free space the run insists on keeping in hand. The library is ~82 MB; refusing to start the
 * long tail without room for it is better than filling the quota and failing on a save the
 * user cares about.
 */
const REQUIRED_FREE_BYTES = 120 * 1024 * 1024;

export type LibraryPhase = 'idle' | 'running' | 'paused' | 'complete' | 'blocked';

export interface LibraryGenerationState {
  phase: LibraryPhase;
  /** Sounds finished in this run. */
  done: number;
  /** Sounds this run set out to make. */
  total: number;
  /** Name of the sound currently rendering, for an honest progress line. */
  current: string;
  /** Bytes written in this run. */
  bytes: number;
  /** A finished sentence when the run stopped for a reason worth explaining. */
  message: string;
}

const IDLE: LibraryGenerationState = { phase: 'idle', done: 0, total: 0, current: '', bytes: 0, message: '' };

const stateSignal = signal<LibraryGenerationState>(IDLE);

/** What the pack browser watches to show progress. */
export const libraryGeneration: ReadonlySignal<LibraryGenerationState> = stateSignal;

let running: Promise<void> | null = null;
let cancelled = false;

function update(change: Partial<LibraryGenerationState>): void {
  stateSignal.value = { ...stateSignal.peek(), ...change };
}

// MARK: - What is already here

/**
 * The library file names already in the open project.
 *
 * Keyed by `originalFileName` — the library's own portable name, e.g. `kick-trap.wav` — and
 * restricted to assets carrying the library tag, so a user's own import that happens to share
 * a name is never mistaken for a generated sound.
 */
export function installedLibraryFileNames(): Set<string> {
  const open = project.peek();
  const names = new Set<string>();
  if (open === null) return names;
  for (const asset of open.assets) {
    if (asset.tags.includes(LIBRARY_TAG)) names.add(asset.originalFileName);
  }
  return names;
}

/** How many of `entries` are already on this device. */
export function installedCount(entries: readonly LibraryEntry[]): number {
  const installed = installedLibraryFileNames();
  let count = 0;
  for (const entry of entries) if (installed.has(entry.fileName)) count++;
  return count;
}

// MARK: - Storage

/**
 * Bytes the browser says are still available, or `null` when it will not say.
 *
 * Safari answers with a per-origin figure that is often a fraction of the disk; treating
 * "unknown" as "plenty" is the only workable default, because refusing to generate on a
 * browser with no Storage API would mean shipping an empty library to it.
 */
async function freeBytes(): Promise<number | null> {
  if (typeof navigator === 'undefined' || navigator.storage?.estimate === undefined) return null;
  try {
    const estimate = await navigator.storage.estimate();
    if (estimate.quota === undefined || estimate.usage === undefined) return null;
    return Math.max(0, estimate.quota - estimate.usage);
  } catch {
    return null;
  }
}

// MARK: - Writing one sound

function editFor(entry: LibraryEntry, duration: number): SampleEdit {
  const edit = sampleEditIdentity();
  if (entry.loops) {
    edit.loopEnabled = true;
    edit.loopStart = 0;
    edit.loopEnd = duration;
    // Deliberately no crossfade. A loop's overhanging tails were already wrapped back onto its
    // own head when it was mixed, so it is seamless as written; a crossfade here would fade
    // down the downbeat that makes it work.
    edit.loopCrossfade = 0;
  }
  return edit;
}

/**
 * Renders one entry, writes its bytes, and returns the asset record.
 *
 * Sixteen-bit PCM rather than twenty-four: every sound is levelled to −1 dBFS with no
 * headroom left to recover, the whole library is synthetic so there is no noise floor to
 * preserve, and the choice is the difference between eighty megabytes and a hundred and
 * twenty on a phone.
 */
async function renderAndStore(
  entry: LibraryEntry,
  sampleRate: number,
  budget: RenderBudget,
): Promise<{ asset: SampleAsset; bytes: number }> {
  // The id is derived from the catalogue entry, not drawn at random, so the same sound has
  // the same identity in every project on this device — which is what lets its bytes be
  // shared instead of rendered and stored again for each one.
  const id = libraryUUID(LIBRARY_ID_SCOPE, entry.id, 0);
  const key = libraryAssetKey(id);

  let blob: Blob;
  let audio: RenderedAudio | null = null;
  const existing = await blobStore().tryGet(key);
  if (existing !== undefined && existing.size > 0) {
    // Already on this device from another project. Rendering it again would produce the same
    // bytes at the cost of a few hundred milliseconds of main thread per sound.
    blob = existing;
  } else {
    audio = await entry.render({ sampleRate, budget });
    blob = encodeWav(audio, 16);
    await blobStore().put(key, blob, { mimeType: 'audio/wav' });
  }

  const decodedRate = audio?.sampleRate ?? sampleRate;
  const frames = audio?.channels[0]?.length ?? Math.max(0, Math.round(((blob.size - 44) / 2) ));
  const duration = frames / decodedRate;
  const hash = await analysis.hash(blob).catch(() => '');

  const asset = makeSampleAsset({
    id,
    name: entry.name,
    fileName: `${id}.wav`,
    // The library's own portable name. This is the key the resume check reads, and it is what
    // a package export writes into the archive.
    originalFileName: entry.fileName,
    origin: 'synthesised',
    duration,
    sampleRate: decodedRate,
    channelCount: audio?.channels.length ?? 1,
    frameCount: frames,
    byteSize: blob.size,
    contentHash: hash,
    defaultEdit: editFor(entry, duration),
    tags: [LIBRARY_TAG, ...entry.tags],
    // Generated on this device from PadLab's own synthesiser: nothing was licensed in, so a
    // pack built from these needs no clearance and the publish guard lets it through.
    licence: libraryContentLicence(),
    caption: entry.caption,
    ...(entry.detectedBPM === undefined ? {} : { detectedBPM: entry.detectedBPM }),
  });
  return { asset, bytes: blob.size };
}

function commit(assets: SampleAsset[], projectID: UUID): void {
  if (assets.length === 0) return;
  const open = project.peek();
  if (open === null || open.id !== projectID) return;
  const pending = [...assets];
  // Silent: adding a sound to the library changes nothing about how the board sounds, and a
  // full engine resync per batch would be twenty-five needless pad reloads. Out of history:
  // the user did not do this, and it must not push their own edits off the undo stack.
  mutate('Add Library Sounds', (target) => {
    target.assets.push(...pending);
  }, { silent: true, history: false });
}

// MARK: - The board
//
// Nothing in this module writes to a pad, and that is deliberate.
//
// An earlier version dropped a sixteen-piece "instant kit" onto bank 1 whenever the library
// was not yet complete, overwriting `assetID`, `name`, `color`, `symbolName`, `edit` and
// `chokeGroup` on every slot. Its guard was `installedLibraryFileNames().size === 0` — a
// fact about the *open project's* assets — while the gate that decides whether generation
// runs at all is the global `libraryContentVersion`. The two disagree in exactly the
// situations the brief names as unacceptable: a run interrupted by backgrounding, the
// low-storage `blocked` path, and a future content-version bump. Any project opened in that
// window had its board silently rewritten, with no reachable undo, which also made the
// template chooser cosmetic — a "Blank Board" came back holding twelve drums.
//
// The board belongs to the user. Templates put the starter sounds there (see
// `createProjectFromTemplate`), the library fills the *library*, and the two never argue.

// MARK: - The run

export interface GenerateLibraryOptions {
  /** Restrict the run to these entries. Defaults to the whole catalogue in pack order. */
  entries?: readonly LibraryEntry[];
  /** Named in the progress line and the finishing notice. */
  label?: string;
}

/**
 * Starts (or resumes) generation. Returns immediately; watch `libraryGeneration` for progress.
 *
 * Calling it while a run is in flight is a no-op rather than an error, because both the
 * first-run path and a pack card can reach it and neither should have to know about the other.
 */
export function startLibraryGeneration(options: GenerateLibraryOptions = {}): void {
  if (running !== null) return;
  cancelled = false;
  running = run(options)
    .catch((error: unknown) => {
      log.error('Library generation failed', error);
      update({
        phase: 'paused',
        message: 'PadLab stopped part-way through building the sound library. The sounds it had already made are in your library, and it will carry on next time you open the app.',
      });
    })
    .finally(() => {
      running = null;
    });
}

/** Stops the run after the sound that is currently rendering. */
export function stopLibraryGeneration(): void {
  if (running === null) return;
  cancelled = true;
  update({ phase: 'paused', message: 'Paused. Tap a pack to carry on whenever you like.' });
}

export function isGeneratingLibrary(): boolean {
  return running !== null;
}

async function run(options: GenerateLibraryOptions): Promise<void> {
  const open = project.peek();
  if (open === null) return;
  const projectID = open.id;

  const requested = options.entries ?? libraryInstallOrder();
  const installed = installedLibraryFileNames();
  const outstanding = requested.filter((entry) => !installed.has(entry.fileName));

  if (outstanding.length === 0) {
    update({ ...IDLE, phase: 'complete', total: requested.length, done: requested.length });
    return;
  }

  const available = await freeBytes();
  if (available !== null && available < REQUIRED_FREE_BYTES) {
    update({
      ...IDLE,
      phase: 'blocked',
      message: `This browser has about ${Math.round(available / 1048576)} MB of space left, and the sound library needs around 120 MB. Free some space, then open the Library tab to try again.`,
    });
    return;
  }

  const sampleRate = engine.sampleRate.peek() > 0 ? engine.sampleRate.peek() : 48000;
  // Six milliseconds is under half a frame at 60 fps, so a render never costs a dropped frame
  // even while the user is playing pads over the top of it.
  const budget = new RenderBudget({ sliceMs: 6 });

  update({
    phase: 'running',
    done: 0,
    total: outstanding.length,
    current: outstanding[0]?.name ?? '',
    bytes: 0,
    message: '',
  });
  log.info('Library generation started', { sounds: outstanding.length, rate: sampleRate });

  let batch: SampleAsset[] = [];
  let done = 0;
  let bytes = 0;

  for (const entry of outstanding) {
    if (cancelled) break;
    // The user moved to another project. Its library is not this one's business.
    if (project.peek()?.id !== projectID) {
      update({ phase: 'paused', message: '' });
      break;
    }

    update({ current: entry.name });
    try {
      const result = await renderAndStore(entry, sampleRate, budget);
      batch.push(result.asset);
      bytes += result.bytes;
      done++;
    } catch (error) {
      // One sound that will not render must not cost the other three hundred. It is left out
      // of the project, so the next run tries it again.
      log.warn('Library sound skipped', { sound: tag(entry.id), reason: tag(error instanceof Error ? error.name : 'unknown') });
    }

    if (batch.length >= BATCH_SIZE) {
      commit(batch, projectID);
      batch = [];
    }

    update({ done, bytes });
    await budget.tick();
  }

  commit(batch, projectID);

  if (cancelled) {
    log.info('Library generation paused', { done, total: outstanding.length });
    return;
  }

  const complete = done >= outstanding.length;
  update({
    phase: complete ? 'complete' : 'paused',
    done,
    current: '',
    message: complete ? '' : 'Some sounds could not be made. Open the Library tab to try the rest again.',
  });

  if (complete && options.entries === undefined) {
    updateSettings({ libraryContentVersion: LIBRARY_CONTENT_VERSION });
  }
  log.info('Library generation finished', { done, bytes });

  if (complete) {
    pushBanner({
      kind: 'success',
      title: options.label === undefined ? 'Your sound library is ready' : `${options.label} installed`,
      message: `${done} sound${done === 1 ? '' : 's'} generated on this device. Every one is yours to edit, export and publish.`,
      timeout: 4000,
    });
  }
}

// MARK: - Entry points

/**
 * The first-run path.
 *
 * Fills the *library* — never the board. Safe to call on every launch: it resumes an
 * interrupted run where it stopped and does nothing once the library is complete.
 */
export function ensureLibraryInstalled(): void {
  if (project.peek() === null) return;
  if (settings.peek().libraryContentVersion >= LIBRARY_CONTENT_VERSION) return;
  startLibraryGeneration();
}

/** Installs one pack — the pack browser's action. */
export function installLibraryPack(packID: string): void {
  const pack = libraryPack(packID);
  if (pack === undefined) return;
  startLibraryGeneration({ entries: pack.entries, label: pack.name });
}

/** The shelf, re-exported so a screen never has to reach past this module into the audio
 * layer to draw a pack card. */
export { LIBRARY_PACKS };
